import React from 'react'
import { Button, Dropdown, MenuProps, Space } from 'antd'
import { Copy, CornerDownRight, EllipsisVertical, LayersPlus, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CustomCellRendererProps } from 'ag-grid-react'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { canRenameOrDelete } from '../utils/dimensionAccess'
import { DimensionGridContext } from './columnDefs/dimensionColumnDefs'
import LucideIcon from '../../../../../shared/components/LucideIcon'

const DimensionRowMenuCellRenderer: React.FC<CustomCellRendererProps<Dimension>> = ({ data, context: rawContext }) => {
  const { t } = useTranslation()
  const context = rawContext as DimensionGridContext

  if (!data || !context?.canEdit) return null

  const canOwn = canRenameOrDelete(data, context.canEdit)
  const hasChildren = context.flatList.some(d => d.parentDimensionId === data.dimensionId)

  const items: MenuProps['items'] = [
    {
      key: 'rename',
      label: t('global:edit'),
      icon: <LucideIcon icon={Pencil} />,
      disabled: !canOwn,
      onClick: () => context.onRequestRename(data)
    },
    {
      key: 'moveUnder',
      label: t('dimensionsPage:actionMoveUnder'),
      icon: <LucideIcon icon={CornerDownRight} />,
      onClick: () => context.onRequestMoveUnder([data.dimensionId])
    },
    {
      key: 'addChild',
      label: t('dimensionsPage:actionAddChild'),
      icon: <LucideIcon icon={LayersPlus} />,
      onClick: () => context.onRequestAddChild(data.dimensionId)
    },
    {
      key: 'duplicate',
      label: t('dimensionsPage:actionDuplicate'),
      icon: <LucideIcon icon={Copy} />,
      disabled: !canOwn,
      onClick: () => context.onDuplicate(data)
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: hasChildren ? t('dimensionsPage:deleteBlockedChildrenLabel') : t('actions:delete'),
      icon: <LucideIcon icon={Trash2} />,
      danger: true,
      disabled: !canOwn || hasChildren,
      onClick: () => context.onRequestDelete([data.dimensionId])
    }
  ]

  return (
    <Space size={2}>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button
          type="text"
          size="small"
          icon={<LucideIcon icon={EllipsisVertical} />}
          onClick={e => e.stopPropagation()}
        />
      </Dropdown>
    </Space>
  )
}

export default DimensionRowMenuCellRenderer
